import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { AccordionComponent } from '../../../shared/components/accordion/accordion.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { Router, ActivatedRoute } from '@angular/router';
import { ClaimsService } from '../../../services/claims.service';
import { SectionsService } from '../../../services/sections.service';
import { UserService } from '../../../services/user.service';
import { BaseComponent } from '../../../shared/utils/base.component';
import { LookupValueItem } from '../../../interfaces/lookup.interface';
import { BillingService } from '../../../services/billing.service';
import {
  DialogConfig,
  DialogService,
} from '../../../shared/services/dialog.service';
import { performValidation } from '../../../shared/utils/validation.util';
import { DropdownSelectComponent } from '../../../shared/components/dropdown-select/dropdown-select.component';

@Component({
  selector: 'ppl-add-config',
  standalone: true,
  imports: [
    ButtonComponent,
    NzFormModule,
    ReactiveFormsModule,
    FormsModule,
    NzCardModule,
    NzInputModule,
    NzToolTipModule,
    CommonModule,
    NzDatePickerModule,
    NzToolTipModule,
    NzSelectModule,
    NzButtonModule,
    AccordionComponent,
    DropdownSelectComponent
  ],
  templateUrl: './add-config.component.html',
  styleUrl: './add-config.component.css',
})
export class AddConfigComponent extends BaseComponent implements OnInit {
  pageTitle = 'Add New Application Config';
  mode = 'edit';
  configForm!: FormGroup;
  programName: any;
  moduleList: any;
  categoryList: any;
  orgTypeList: any = [];
  orgNameList: any = [];
  savedData: any = {};
  radioOptions: LookupValueItem[] = [];
  isNew = true;
  private dialogSvc = inject(DialogService);
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;

  constructor(
    private fb: FormBuilder,
    private sectionsService: SectionsService,
    private router: Router,
    private route: ActivatedRoute,
    private claimsService: ClaimsService,
    private userService: UserService,
    private billingService: BillingService
  ) {
    super();
  }

  ngOnInit(): void {
    this.configForm = this.fb.group({
      category: [''],
      subCategory: [''],
      configDesc: [''],
      configKey: [''],
      configValue: [''],
      isActive: [true],
      module: [''],
      orgName: [''],
      orgType: [''],
    });

    const program = this.userService.getSelectedProgram();
    this.programName = program?.program_code;
    this.secData = this.sectionsService.getSectionData(
      this.programName,
      'config'
    );
    this.lookupService.getOrgType(this.programName).subscribe((data: any) => {
      this.orgTypeList = data.responsedata;
    });
    this.categoryList = this.getLookup('config_category');
    this.moduleList = this.getLookup('config_module');
    this.radioOptions = this.getLookup('Yes_No');
    this.savedData = this.claimsService.getFormData('config');

    this.route.paramMap.subscribe((params) => {
      const action = params.get('action')!;
      this.pageTitle =
        action == 'update'
          ? `Details for ID: ${this.savedData?.ngAppConfigId}`
          : this.pageTitle;
      this.isNew = action === 'add';
      this.mode = action == 'update' ? 'read' : 'edit';
    });
    this.configForm.get('orgType')?.valueChanges.subscribe((value) => {
      this.orgNameList = [];
      if (value) {
        this.fetchDataByOrgType(value);
      }
    });
  }

  fetchDataByOrgType(value: any) {
    this.lookupService
      .getOrgName(this.programName, value)
      .subscribe((data: any) => {
        this.orgNameList = data.responsedata;
      });
  }

  onCancel() {
    this.mode = 'read';
    this.validationErrors = {};
    this.configForm.reset();
  }

  onUpdate() {}

  goBack() {
    this.router.navigate(['/home/configList']);
  }

  onDelete() {
    this.openConfirmationPopup();
  }

  onAdd(action: string) {
    this.validationErrors = performValidation(
          this.configForm.getRawValue(),
          this.secData,
          'Next', undefined, undefined, this.sectionsService.getFlow(), this.sectionsService.getEnrollmentType()
        );
    const payload = {
      ...this.configForm.getRawValue(),
      programCode: this.programName,
      ngAppConfigId: action === 'add' ? '0' : this.savedData?.ngAppConfigId,
      isActive: action === 'add' ? true : this.savedData?.isActive,
    };
    if (Object.keys(this.validationErrors).length === 0) {
    this.billingService
      .addAndUpdateConfig(payload, action)
      .subscribe((data: any) => {
        this.router.navigate(['/home/configList']);
      });
    }
    else{
      return;
    }
  }

  onEdit() {
    this.mode = 'edit';
    const data = this.savedData;
    this.configForm.patchValue(data);
    this.configForm
      .get('category')
      ?.setValue(data.category, { emitEvent: false });
    this.configForm.get('module')?.setValue(data.module, { emitEvent: false });
    this.configForm.get('category')?.disable({ emitEvent: false });
    this.configForm.get('module')?.disable({ emitEvent: false });
  }

  openConfirmationPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      isOkText: 'Ok',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
    };

    this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => this.deleteAuth(),
      () => this.handleCancel()
    );
  }

  handleCancel() {}

  deleteAuth() {
    this.billingService.deleteConfig(this.programName, this.savedData.ngAppConfigId).subscribe((data: any)=>{
      if(data.status === 200){
        this.router.navigate(['/home/configList']);
      }
    })
  }
}
